import { useLayoutEffect, useMemo, useState, useTransition } from 'react';

import { useIntersectionObserver } from '@spazious/react-hooks';

import { StickyContextValue } from './StickyContext.js';
import { useStickyContext } from './useStickyContext.js';

export interface StickyHookOptions {
  anchor?: string;
}

export const findStickyContainerById = (node: StickyContextValue, id?: string) => {
  for (;;) {
    if (node.id === id) {
      return node;
    }

    if (!node.parent) {
      return node;
    }

    node = node.parent;
  }
};

export const useSticky = <T = Element>(opts: StickyHookOptions) => {
  const { anchor } = opts;
  const [isPending, startTransition] = useTransition();
  const [intersectionRef, observer] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  }) as [React.RefObject<T>, IntersectionObserverEntry | undefined];

  const [stickyState, setStickyState] = useState<{
    top: number;
    left: number;
    right: number;
    bottom: number;
    isSticky: boolean;
  }>();

  const contextValue = useStickyContext();

  useLayoutEffect(() => {
    const stickyContainer = findStickyContainerById(contextValue, anchor);
    startTransition(() => {
      setStickyState({
        top: stickyContainer.container.y,
        left: stickyContainer.container.x,
        right: stickyContainer.container.x + stickyContainer.container.width,
        bottom: stickyContainer.container.y + stickyContainer.container.height,
        isSticky: !!observer?.isIntersecting,
      });
    });
  }, [anchor, contextValue, observer?.isIntersecting]);

  const stickyResult = useMemo(() => ({ ...stickyState, isPending }), [isPending, stickyState]);

  return [intersectionRef, stickyResult] as const;
};
